/**
 * Unicode text formatting utility for LinkedIn posts.
 *
 * LinkedIn does not support native bold / italic / underline in post text.
 * The standard workaround is to replace ASCII characters with their
 * Unicode Mathematical equivalents.
 *
 * Ranges used:
 *   Bold uppercase       : U+1D400 – U+1D419  (𝐀–𝐙)
 *   Bold lowercase       : U+1D41A – U+1D433  (𝐚–𝐳)
 *   Bold digits          : U+1D7CE – U+1D7D7  (𝟎–𝟗)
 *
 *   Italic uppercase     : U+1D434 – U+1D44D  (𝐴–𝑍)  — h exception: U+210E
 *   Italic lowercase     : U+1D44E – U+1D467  (𝑎–𝑧)
 *
 *   Underline            : U+0332 combining low line appended to each char
 */

// ── Bold ────────────────────────────────────────────────────────────────

const BOLD_UPPER_START = 0x1d400;
const BOLD_LOWER_START = 0x1d41a;
const BOLD_DIGIT_START = 0x1d7ce;

function charToBold(ch: string): string {
  const code = ch.charCodeAt(0);
  if (code >= 65 && code <= 90) return String.fromCodePoint(BOLD_UPPER_START + (code - 65));
  if (code >= 97 && code <= 122) return String.fromCodePoint(BOLD_LOWER_START + (code - 97));
  if (code >= 48 && code <= 57) return String.fromCodePoint(BOLD_DIGIT_START + (code - 48));
  return ch;
}

function charFromBold(codePoint: number): string | null {
  if (codePoint >= BOLD_UPPER_START && codePoint <= BOLD_UPPER_START + 25) {
    return String.fromCharCode(65 + (codePoint - BOLD_UPPER_START));
  }
  if (codePoint >= BOLD_LOWER_START && codePoint <= BOLD_LOWER_START + 25) {
    return String.fromCharCode(97 + (codePoint - BOLD_LOWER_START));
  }
  if (codePoint >= BOLD_DIGIT_START && codePoint <= BOLD_DIGIT_START + 9) {
    return String.fromCharCode(48 + (codePoint - BOLD_DIGIT_START));
  }
  return null;
}

export function isBoldChar(ch: string): boolean {
  return charFromBold(ch.codePointAt(0)!) !== null;
}

// ── Italic ──────────────────────────────────────────────────────────────

const ITALIC_UPPER_START = 0x1d434;
const ITALIC_LOWER_START = 0x1d44e;
const ITALIC_H_EXCEPTION = 0x210e; // ℎ — Planck constant

function charToItalic(ch: string): string {
  const code = ch.charCodeAt(0);
  if (code >= 65 && code <= 90) return String.fromCodePoint(ITALIC_UPPER_START + (code - 65));
  if (code >= 97 && code <= 122) {
    // 'h' is at index 7, but U+1D455 is unassigned — use U+210E instead
    if (code === 104) return String.fromCodePoint(ITALIC_H_EXCEPTION);
    return String.fromCodePoint(ITALIC_LOWER_START + (code - 97));
  }
  return ch; // digits have no italic variant — keep as-is
}

function charFromItalic(codePoint: number): string | null {
  if (codePoint >= ITALIC_UPPER_START && codePoint <= ITALIC_UPPER_START + 25) {
    return String.fromCharCode(65 + (codePoint - ITALIC_UPPER_START));
  }
  if (codePoint === ITALIC_H_EXCEPTION) return 'h';
  if (codePoint >= ITALIC_LOWER_START && codePoint <= ITALIC_LOWER_START + 25) {
    // Skip the gap at U+1D455 (index 7 = 'h')
    return String.fromCharCode(97 + (codePoint - ITALIC_LOWER_START));
  }
  return null;
}

export function isItalicChar(ch: string): boolean {
  return charFromItalic(ch.codePointAt(0)!) !== null;
}

// ── Underline (combining character) ─────────────────────────────────────

const COMBINING_LOW_LINE = '\u0332';

function charToUnderline(ch: string): string {
  // Don't underline whitespace
  if (/\s/.test(ch)) return ch;
  return ch + COMBINING_LOW_LINE;
}

export function isUnderlineChar(ch: string): boolean {
  return ch.length >= 2 && ch.includes(COMBINING_LOW_LINE);
}

// ── Generic toggle helpers ──────────────────────────────────────────────

function isNonAlphanumeric(ch: string): boolean {
  return /[\s\p{P}\p{S}]/u.test(ch);
}

/**
 * Strip all Unicode formatting (bold, italic, underline) back to plain ASCII.
 * This is essential so that switching between styles works correctly
 * (e.g. bold text can be converted to italic).
 */
export function toPlainText(text: string): string {
  // 1. Remove combining underline characters
  let plain = text.replace(new RegExp(COMBINING_LOW_LINE, 'g'), '');
  // 2. Convert bold / italic chars back to ASCII
  return [...plain]
    .map((ch) => {
      const cp = ch.codePointAt(0)!;
      return charFromBold(cp) ?? charFromItalic(cp) ?? ch;
    })
    .join('');
}

// ── Public API ──────────────────────────────────────────────────────────

export function toBold(text: string): string {
  return [...text].map(charToBold).join('');
}

export function fromBold(text: string): string {
  return [...text].map((ch) => charFromBold(ch.codePointAt(0)!) ?? ch).join('');
}

export function toItalic(text: string): string {
  return [...text].map(charToItalic).join('');
}

export function fromItalic(text: string): string {
  return [...text].map((ch) => charFromItalic(ch.codePointAt(0)!) ?? ch).join('');
}

export function toUnderline(text: string): string {
  return [...text].map(charToUnderline).join('');
}

export function fromUnderline(text: string): string {
  return text.replace(new RegExp(COMBINING_LOW_LINE, 'g'), '');
}

/** Toggle bold on a selection. Strips other formatting first. */
export function toggleBold(text: string): string {
  const chars = [...text];
  const allBold = chars.length > 0
    && chars.every((ch) => isNonAlphanumeric(ch) || isBoldChar(ch))
    && chars.some(isBoldChar);
  if (allBold) return toPlainText(text);
  // Strip any existing formatting, then apply bold
  return toBold(toPlainText(text));
}

/** Toggle italic on a selection. Strips other formatting first. */
export function toggleItalic(text: string): string {
  const chars = [...text];
  const allItalic = chars.length > 0
    && chars.every((ch) => isNonAlphanumeric(ch) || isItalicChar(ch))
    && chars.some(isItalicChar);
  if (allItalic) return toPlainText(text);
  // Strip any existing formatting, then apply italic
  return toItalic(toPlainText(text));
}

/** Toggle underline on a selection. Strips other formatting first. */
export function toggleUnderline(text: string): string {
  const hasUL = text.includes(COMBINING_LOW_LINE);
  if (hasUL) return fromUnderline(text);
  // Strip bold/italic first, then apply underline
  const plain = toPlainText(text);
  return toUnderline(plain);
}
